import React, {useState} from 'react';

import {Button, ChevronDownIcon, ChevronUpIcon, Collapse, SmallLoader} from '@kit';

import {cls, getApolloError} from '../../../../components/utils';
import {Markdown} from '../../../../components/utils/md';
import {useGlossaryItemQuery} from '../../../../generated/types';
import {ItemDescription} from './item-description';
import {SpellDescription} from './spell-description';

import css from './sheet.module.css';

export type RowDescription =
	| {kind: 'glossary'; id: string}
	| {kind: 'spell'; id: string}
	| {kind: 'item'; id: string}
	| {kind: 'text'; text: string};

type Props = {
	title: React.ReactNode;
	/** мелкая подпись после названия */
	note?: React.ReactNode;
	/** строка под названием: атака, урон, дальность */
	details?: React.ReactNode;
	/** счётчики и кнопки справа */
	aside?: React.ReactNode;
	description?: RowDescription;
	className?: string;
};

export const ExpandableRow: React.FC<Props> = ({title, note, details, aside, description, className}) => {
	const [isOpen, setOpen] = useState(false);

	return (
		<li className={cls(css.row, className)}>
			<div className={css.rowMain}>
				<div
					className={cls(css.rowLead, description && css.expandable)}
					onClick={description ? expandOnClick(() => setOpen(!isOpen)) : undefined}
				>
					{description ? (
						<ExpandButton isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />
					) : (
						<span className={css.expandPlaceholder} />
					)}
					<div className={css.rowText}>
						<span className={css.rowTitle}>
							{title}
							{note && <span className={css.muted}> {note}</span>}
						</span>
						{details && <div className={css.rowDetails}>{details}</div>}
					</div>
				</div>
				{aside && <div className={css.rowAside}>{aside}</div>}
			</div>
			{description && (
				<Collapse open={isOpen}>
					<RowDescriptionView description={description} />
				</Collapse>
			)}
		</li>
	);
};

const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [role="checkbox"]';

/** нажатие по области названия раскрывает строку; вложенные кнопки и флажки работают сами по себе */
export const expandOnClick = (toggle: () => void) => (event: React.MouseEvent) => {
	if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
	if (window.getSelection()?.toString()) return;
	toggle();
};

type ExpandButtonProps = {
	isOpen: boolean;
	onToggle(): void;
};

/** стрелка слева от названия: раскрывает описание строки */
export const ExpandButton: React.FC<ExpandButtonProps> = ({isOpen, onToggle}) => (
	<Button
		size="small"
		appearance="subtle"
		className={css.expandButton}
		icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
		aria-label={isOpen ? 'Скрыть описание' : 'Показать описание'}
		aria-expanded={isOpen}
		onClick={onToggle}
	/>
);

export const RowDescriptionView: React.FC<{description: RowDescription}> = ({description}) => {
	if (description.kind === 'spell') return <SpellDescription spellId={description.id} />;
	if (description.kind === 'item') return <ItemDescription itemId={description.id} />;
	if (description.kind === 'glossary') return <GlossaryDescription id={description.id} />;
	return (
		<div className={css.rowDescription}>
			<Markdown text={description.text} />
		</div>
	);
};

type GlossaryDescriptionProps = {
	id: string;
	className?: string;
};

export const GlossaryDescription: React.FC<GlossaryDescriptionProps> = ({id, className = css.rowDescription}) => {
	const {data, loading, error} = useGlossaryItemQuery({variables: {id}});
	if (loading) return <SmallLoader />;
	if (error) return <div className={css.muted}>{getApolloError(error).message}</div>;
	return (
		<div className={className}>
			<Markdown text={data?.glossaryItem.description || ''} />
		</div>
	);
};
